import React, { useEffect, useRef, useCallback } from 'react';

const DEG        = Math.PI / 180;
const PERSP      = 900;
const TRAIL_ANGLE  = Math.PI * 1.1;  // ~198° arc behind the electron

// Fewer trail segments on mobile for performance
const IS_MOBILE    = typeof window !== 'undefined' && window.innerWidth < 768;
const TRAIL_STEPS  = IS_MOBILE ? 22 : 40;

const orbitals = [
  {
    rotX: 72, rotY: 0,
    durationMs: 2400,
    phaseOffset: 0,
    trail:        [59,  130, 246],
    electronGlow: '0 0 10px rgba(59,130,246,1), 0 0 5px rgba(255,255,255,0.5)',
  },
  {
    rotX: 72, rotY: 60,
    durationMs: 3600,
    phaseOffset: (2 * Math.PI) / 3,
    trail:        [99,  102, 241],
    electronGlow: '0 0 10px rgba(99,102,241,1), 0 0 5px rgba(255,255,255,0.45)',
  },
  {
    rotX: 72, rotY: -60,
    durationMs: 3000,
    phaseOffset: (4 * Math.PI) / 3,
    trail:        [147, 197, 253],
    electronGlow: '0 0 10px rgba(147,197,253,0.95), 0 0 5px rgba(255,255,255,0.45)',
  },
];

// Pre-compute trig values per orbital (they never change)
const orbitalTrig = orbitals.map(o => ({
  cosRY: Math.cos(o.rotY * DEG),
  sinRY: Math.sin(o.rotY * DEG),
  cosRX: Math.cos(o.rotX * DEG),
  sinRX: Math.sin(o.rotX * DEG),
}));

function project(θ, oi, R, cx, cy) {
  const lx = R * Math.cos(θ);
  const ly = R * Math.sin(θ);
  const { cosRY, sinRY, cosRX, sinRX } = orbitalTrig[oi];
  const wx  =  lx * cosRY;
  const wz  = -lx * sinRY;
  const wy  = ly * cosRX - wz * sinRX;
  const wz2 = ly * sinRX + wz * cosRX;
  const pScale = PERSP / (PERSP - wz2);
  return { x: cx + wx * pScale, y: cy + wy * pScale, pScale, z: wz2 };
}

const AtomOrbit = () => {
  const containerRef   = useRef(null);
  const canvasRef      = useRef(null);
  const canvasFrontRef = useRef(null);
  const electronRefs   = useRef([null, null, null]);
  const rafRef         = useRef(null);
  const startRef       = useRef(null);
  const sizeRef        = useRef(0);       // cached container size
  const dprRef         = useRef(1);       // cached devicePixelRatio

  // Cache container size via ResizeObserver — no offsetWidth reads in rAF
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      sizeRef.current = entry.contentRect.width;
    });
    ro.observe(el);
    sizeRef.current = el.offsetWidth; // initial read (once)
    dprRef.current  = window.devicePixelRatio || 1;
    return () => ro.disconnect();
  }, []);

  const animate = useCallback((timestamp) => {
    if (!startRef.current) startRef.current = timestamp;
    const elapsed = timestamp - startRef.current;

    const canvas      = canvasRef.current;
    const canvasFront = canvasFrontRef.current;
    const size = sizeRef.current;
    if (!canvas || !canvasFront || size === 0) {
      rafRef.current = requestAnimationFrame(animate);
      return;
    }

    const dpr      = dprRef.current;
    const pxSize   = Math.round(size * dpr);   // physical pixel size

    // Resize canvas backing store to match DPR, CSS size stays the same
    if (canvas.width !== pxSize || canvas.height !== pxSize) {
      canvas.width  = pxSize;
      canvas.height = pxSize;
      canvas.style.width  = size + 'px';
      canvas.style.height = size + 'px';
    }
    if (canvasFront.width !== pxSize || canvasFront.height !== pxSize) {
      canvasFront.width  = pxSize;
      canvasFront.height = pxSize;
      canvasFront.style.width  = size + 'px';
      canvasFront.style.height = size + 'px';
    }

    const ctx      = canvas.getContext('2d');
    const ctxFront = canvasFront.getContext('2d');
    ctx.clearRect(0, 0, pxSize, pxSize);
    ctxFront.clearRect(0, 0, pxSize, pxSize);

    // Scale drawing coordinates so project() still works in CSS-pixel space
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctxFront.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cx    = size / 2;
    const cy    = size / 2;
    const R     = (size * 0.97) / 2;
    const eBase = size * 0.135;

    orbitals.forEach((o, i) => {
      const θ = ((elapsed % o.durationMs) / o.durationMs) * 2 * Math.PI + o.phaseOffset;
      const [r, g, b] = o.trail;

      // Pre-compute all sample points
      const pts = [];
      for (let j = 0; j <= TRAIL_STEPS; j++) {
        const t = j / TRAIL_STEPS;
        pts.push(project(θ - t * TRAIL_ANGLE, i, R, cx, cy));
      }

      for (let j = 0; j < TRAIL_STEPS; j++) {
        const t     = j / TRAIL_STEPS;
        const T_PEAK = 0.08;
        const alpha = t < T_PEAK ? t / T_PEAK : Math.exp(-3.5 * (t - T_PEAK));

        const p0 = pts[j];
        const p1 = pts[j + 1];
        const sc = (p0.pScale + p1.pScale) * 0.5;
        const segZ = (p0.z + p1.z) * 0.5;
        const c = segZ > 0 ? ctxFront : ctx;

        // Glow pass
        c.beginPath();
        c.moveTo(p0.x, p0.y);
        c.lineTo(p1.x, p1.y);
        c.strokeStyle = `rgba(${r},${g},${b},${(alpha * 0.35).toFixed(3)})`;
        c.lineWidth   = Math.max(0.5, 5 * (1 - t * 0.6) * sc);
        c.lineCap     = 'round';
        c.stroke();

        // Core pass
        c.beginPath();
        c.moveTo(p0.x, p0.y);
        c.lineTo(p1.x, p1.y);
        c.strokeStyle = `rgba(${r},${g},${b},${(alpha * 0.95).toFixed(3)})`;
        c.lineWidth   = Math.max(0.3, 1.4 * (1 - t * 0.5) * sc);
        c.lineCap     = 'round';
        c.stroke();
      }

      // Electron position — use transform (GPU-composited, no layout/paint)
      const head    = pts[0];
      const visSize = eBase * head.pScale;
      const el      = electronRefs.current[i];
      if (el) {
        const tx = head.x - visSize / 2;
        const ty = head.y - visSize / 2;
        el.style.transform = `translate3d(${tx}px,${ty}px,0)`;
        el.style.width  = `${visSize}px`;
        el.style.height = `${visSize}px`;
        el.style.zIndex = head.z < 0 ? '5' : '15';
      }
    });

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [animate]);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', position: 'relative' }}
    >
      {/* Nucleus — uses box-shadow instead of filter: drop-shadow for perf */}
      <img
        src="/spheres-moving.png"
        alt="Nucleus"
        style={{
          position:  'absolute',
          width:     '36%',
          height:    '36%',
          top:       '32%',
          left:      '32%',
          objectFit: 'contain',
          zIndex:    10,
          filter:
            'drop-shadow(0 0 16px rgba(59,130,246,0.75)) ' +
            'drop-shadow(0 0 32px rgba(59,130,246,0.35)) ' +
            'drop-shadow(0 0 6px rgba(255,255,255,0.28))',
          willChange: 'auto',
        }}
      />

      {/* Trail canvases — back (z 8) and front (z 12) relative to nucleus */}
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, zIndex: 8, pointerEvents: 'none' }}
      />
      <canvas
        ref={canvasFrontRef}
        style={{ position: 'absolute', inset: 0, zIndex: 12, pointerEvents: 'none' }}
      />

      {/* Electrons — positioned via transform: translate3d (GPU layer) */}
      {orbitals.map((o, i) => (
        <img
          key={i}
          ref={el => { electronRefs.current[i] = el; }}
          src="/spheres-moving.png"
          alt=""
          aria-hidden
          style={{
            position:  'absolute',
            top:       0,
            left:      0,
            objectFit: 'contain',
            boxShadow: o.electronGlow,
            borderRadius: '50%',
            willChange: 'transform',
            width:     0,
            height:    0,
          }}
        />
      ))}
    </div>
  );
};

export default AtomOrbit;
